<template>
    <div class="vendor-management-container container">
      <el-card shadow="hover">
        <template #header>
          <div class="card-header">
            <h2>إدارة الموردين</h2>
            <el-button type="primary" @click="handleCreate">
              <el-icon><Plus /></el-icon> إضافة مورد جديد
            </el-button>
          </div>
        </template>
  
        <el-alert
          title="ما هي هذه الواجهة؟"
          type="info"
          description="هذه الواجهة هي دليلك لجميع الموردين والشركات التي تتعامل معها المدرسة (مثل: المكتبات، شركات الصيانة، موردي الأثاث، إلخ). يجب إضافة المورد هنا أولاً قبل أن تتمكن من تسجيل أي فاتورة شراء أو مصروف باسمه."
          :closable="false"
          show-icon
          style="margin-bottom: 20px;"
        />
  
        <el-input
          v-model="searchQuery"
          placeholder="ابحث عن مورد بالاسم..."
          @input="handleSearch"
          clearable
          class="search-input"
        >
          <template #prepend><el-icon><Search /></el-icon></template>
        </el-input>
  
        <el-table :data="vendors" v-loading="loading" border style="width: 100%; margin-top: 20px;">
          <el-table-column prop="name" label="اسم المورد" />
          <el-table-column prop="contact_person" label="اسم جهة الاتصال" />
          <el-table-column prop="phone" label="رقم الهاتف" />
          <el-table-column prop="email" label="البريد الإلكتروني" />
          <el-table-column label="الإجراءات" width="150" fixed="right">
            <template #default="{ row }">
              <el-button size="small" @click="handleEdit(row)">تعديل</el-button>
              <el-popconfirm
                title="هل أنت متأكد من حذف هذا المورد؟"
                @confirm="handleDelete(row.id)"
              >
                <template #reference><el-button size="small" type="danger">حذف</el-button></template>
              </el-popconfirm>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
  
      <!-- Dialog لإنشاء/تعديل المورد -->
      <el-dialog v-model="dialogVisible" :title="dialogTitle" width="40%" :close-on-click-modal="false">
        <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
          <el-form-item label="اسم المورد" prop="name">
            <el-input v-model="form.name" placeholder="مثال: مكتبة الأجيال" />
          </el-form-item>
          <el-form-item label="اسم جهة الاتصال (اختياري)" prop="contact_person">
            <el-input v-model="form.contact_person" />
          </el-form-item>
          <el-form-item label="رقم الهاتف (اختياري)" prop="phone">
            <el-input v-model="form.phone" />
          </el-form-item>
          <el-form-item label="البريد الإلكتروني (اختياري)" prop="email">
            <el-input v-model="form.email" />
          </el-form-item>
        </el-form>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="dialogVisible = false">إلغاء</el-button>
            <el-button type="primary" @click="submitForm" :loading="formLoading">
              {{ form.id ? 'تحديث' : 'إنشاء' }}
            </el-button>
          </span>
        </template>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { ElMessage, ElPopconfirm } from 'element-plus';
  import { Plus, Search } from '@element-plus/icons-vue';
  import debounce from 'lodash/debounce';
  
  export default {
    name: 'VendorManagement',
    components: { Plus, Search, ElPopconfirm },
    data() {
      return {
        api: null,
        loading: true,
        vendors: [],
        searchQuery: '',
        dialogVisible: false,
        formLoading: false,
        form: this.getInitialForm(),
        rules: {
          name: [{ required: true, message: 'اسم المورد مطلوب', trigger: 'blur' }],
          email: [{ type: 'email', message: 'الرجاء إدخال بريد إلكتروني صحيح', trigger: ['blur', 'change'] }]
        },
      };
    },
    computed: {
      dialogTitle() {
        return this.form.id ? 'تعديل بيانات المورد' : 'إضافة مورد جديد';
      },
    },
    created() {
      this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
      this.api.interceptors.request.use((config) => {
        const token = localStorage.getItem('access_token');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        return config;
      });
      this.fetchVendors();
    },
    methods: {
      getInitialForm() {
        return { id: null, name: '', contact_person: '', phone: '', email: '' };
      },
      async fetchVendors() {
        this.loading = true;
        try {
          const response = await this.api.get('/vendors', { params: { search: this.searchQuery } });
          this.vendors = response.data;
        } catch (error) {
          ElMessage.error('فشل في جلب الموردين.');
        } finally {
          this.loading = false;
        }
      },
      handleSearch: debounce(function() {
          this.fetchVendors();
      }, 500),
      handleCreate() {
        this.form = this.getInitialForm();
        this.dialogVisible = true;
      },
      handleEdit(vendor) {
        this.form = { ...vendor };
        this.dialogVisible = true;
      },
      async submitForm() {
        if (!this.$refs.formRef) return;
        await this.$refs.formRef.validate(async (valid) => {
          if (!valid) return;
          this.formLoading = true;
          try {
            if (this.form.id) {
              await this.api.put(`/vendors/${this.form.id}`, this.form);
              ElMessage.success('تم تحديث المورد بنجاح!');
            } else {
              await this.api.post('/vendors', this.form);
              ElMessage.success('تم إنشاء المورد بنجاح!');
            }
            this.dialogVisible = false;
            this.fetchVendors();
          } catch (error) {
            const errorMsg = error.response?.data?.errors ? Object.values(error.response.data.errors)[0][0] : (error.response?.data?.message || 'فشلت العملية.');
            ElMessage.error(errorMsg);
          } finally {
            this.formLoading = false;
          }
        });
      },
      async handleDelete(id) {
        try {
          await this.api.delete(`/vendors/${id}`);
          ElMessage.success('تم حذف المورد بنجاح!');
          this.fetchVendors();
        } catch (error) {
          ElMessage.error(error.response?.data?.message || 'فشل في حذف المورد.');
        }
      },
    },
  };
  </script>
  
  <style scoped>
  .vendor-management-container { padding: 20px; }
  .card-header { display: flex; justify-content: space-between; align-items: center; }
  .search-input { max-width: 400px; }
  </style>
  